import { ReactElement, useCallback } from 'react';

import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';
import { PractitionerDetails, PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';

import { FormLabel, StyledFlex } from '@dreryk/react-components';
import { useFormController } from '@dreryk/react-common';

import { PatientSimpleCreateAction } from '~libs.desktop.shared.patient-simple-create-action';
import { protectFeature } from '~libs.shared.system';

import { translationKeys } from '../translations';

import { PatientField } from './fields/PatientField/PatientField';
import { WaitlistFormBean, WaitlistFormController } from './WaitlistForm.types';
import { PractitionerField } from './fields/PractitionerField/PractitionerField';
import { LocationField } from './fields/LocationField/LocationField';
import { ReservationPreferenceField } from './fields/ReservationPreferenceField/ReservationPreferenceField';
import { NoteField } from './fields/NoteField';
import { UrgentField } from './fields/UrgentField/UrgentField';
import { ServiceField } from './fields/ServiceField/ServiceField';
import { VideoConsultationField } from './fields/VideoConsultationField/VideoConsultationField';
import { AllDaysPreferencesField } from './fields/AllDaysPreferencesField/AllDaysPreferencesField';
import { DayPreferenceField } from './fields/DayPreferenceField/DayPreferenceField';
import { StyledDayPreferences } from './WaitlistForm.styled';
import { DayTimePreferencesField } from './fields/DayTimePreferencesField/DayTimePreferencesField';
import { DayPreferenceFieldBindingCreator } from './fields/DayPreferenceField/DayPreferenceField.types';
import { useDayPreferencesSync } from './WaitlistForm.hooks';
import { DAYS_OF_WEEK } from './fields/AllDaysPreferencesField/AllDaysPreferencesField.constants';

type WaitlistFormChildrenRenderProps = {
   handleSubmit: WaitlistFormController['handleSubmit'];
   formState: WaitlistFormController['formState'];
};

type WaitlistFormProps<BEAN extends WaitlistFormBean> = {
   children: (renderProps: WaitlistFormChildrenRenderProps) => ReactElement;
   practitioners?: PractitionerView[] | undefined;
   currentPractitioner: PractitionerDetails | undefined;
   initialValues?: BEAN;
};

export const WaitlistForm = <BEAN extends WaitlistFormBean>(props: WaitlistFormProps<BEAN>): ReactElement => {
   const {
      register,
      handleSubmit,
      formState,
      setValue,
      trigger,
      watch,
      sync,
      getValue,
   } = useFormController<WaitlistFormBean>(props.initialValues);

   const patient = watch('patient');

   const handlePatientSimpleCreated = useCallback(
      (patient: PatientSimple) => {
         setValue('patient', { ...patient, id: PatientId.of(patient.id) });
         trigger('patient');
      },
      [setValue, trigger]
   );

   const syncDayPreferences = useDayPreferencesSync(getValue, setValue, trigger);

   return (
      <>
         <StyledFlex as={'form'} noValidate direction={'column'} gap={'24px'}>
            <StyledFlex gap={'24px'}>
               <PatientField register={register} />
               {patient ? null : <PatientSimpleCreateAction onSuccess={handlePatientSimpleCreated} />}
            </StyledFlex>
            <UrgentField register={register} />
            {protectFeature('bookingVideoConsultations', <VideoConsultationField register={register} />)}
            <PractitionerField
               register={register}
               practitioners={props.practitioners}
               currentPractitioner={props.currentPractitioner}
            />
            {protectFeature(
               'searchingPrivateMedicalServices',
               <ServiceField register={register} watch={watch} setValue={setValue} />
            )}
            <LocationField register={register} watch={watch} />
            <ReservationPreferenceField register={register} />
            <StyledFlex direction={'column'} gap={'8px'}>
               <FormLabel testId={'daysPreferences'} label={translationKeys.determinePatientAvailability} required />
               <AllDaysPreferencesField register={register} sync={sync} />
               <StyledDayPreferences>
                  {DAYS_OF_WEEK.map((dayOfWeek) => (
                     <DayPreferenceField
                        key={dayOfWeek}
                        register={register as DayPreferenceFieldBindingCreator<typeof dayOfWeek>}
                        dayOfWeek={dayOfWeek}
                        setValue={setValue}
                        watch={watch}
                        formState={formState}
                        trigger={trigger}
                        onChange={syncDayPreferences}
                     />
                  ))}
                  <DayTimePreferencesField register={register} watch={watch} trigger={trigger} />
               </StyledDayPreferences>
            </StyledFlex>
            <NoteField register={register} />
         </StyledFlex>
         {props.children({ handleSubmit: handleSubmit, formState: formState })}
      </>
   );
};
